import { baseApi } from '@app/baseApi';

import type { InviteUsersRequest, TenantUser, User } from '@/types';

import { fromApiUsers } from './users.mappers';

/**
 * Users API - Organization and user management
 *
 * Backend returns PascalCase (Id, Username, Admin, TenantId)
 * Frontend uses camelCase (id, username, admin, tenantid)
 * Mappers handle the transformation
 */
export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get current authenticated user
    getCurrentUser: builder.query<User, void>({
      query: () => ({
        url: 'me',
        endpoint: 'main',
      }),
      transformResponse: (response: Record<string, unknown>) => ({
        id: response.id || response.Id,
        username: response.username || response.Username || '',
        email: response.email || response.Email || '',
        role: response.role || response.Role,
        admin: response.admin || response.Admin,
        tenantid: response.tenantid || response.TenantId,
        phone: response.phone || response.Phone,
        isActive: response.isActive || response.IsActive,
        accepted: response.accepted || response.Accepted,
      }),
      providesTags: ['CurrentUser'],
    }),

    // Get all users (across all tenants user has access to)
    getUsers: builder.query<TenantUser[], void>({
      query: () => ({
        url: 'users',
        endpoint: 'main',
      }),
      transformResponse: (response: unknown) => fromApiUsers(response as Record<string, unknown>[]),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'User' as const, id })),
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }],
    }),

    // Invite users to tenant
    inviteUsersToTenant: builder.mutation<void, { tenantId: string; users: InviteUsersRequest[] }>({
      query: ({ tenantId, users }) => ({
        url: `tenants/${tenantId}/permissions`,
        endpoint: 'main',
        method: 'POST',
        body: users, // Send array directly, not wrapped in object
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    // Update user admin status in tenant
    updateTenantUserAdmin: builder.mutation<
      void,
      { tenantId: string; userId: number; isAdmin: boolean }
    >({
      query: ({ tenantId, userId, isAdmin }) => ({
        url: `tenants/${tenantId}/permissions/${userId}`,
        endpoint: 'main',
        method: 'PUT',
        body: { isAdmin },
      }),
      // Optimistic update for immediate UI feedback
      onQueryStarted: async ({ tenantId, userId, isAdmin }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          usersApi.util.updateQueryData('getUsers', undefined, (draft) => {
            const userIndex = draft.findIndex(
              (u) => u.id === userId && String(u.tenantid) === String(tenantId)
            );
            if (userIndex !== -1) {
              draft[userIndex].admin = isAdmin;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      // Don't invalidate - rely on optimistic update only
      invalidatesTags: [],
    }),

    // Remove user from tenant
    removeUserFromTenant: builder.mutation<void, { tenantId: string; userId: number }>({
      query: ({ tenantId, userId }) => ({
        url: `${tenantId}/users/${userId}/remove`,
        endpoint: 'main',
        method: 'DELETE',
      }),
      // Optimistic update for immediate UI feedback
      onQueryStarted: async ({ userId, tenantId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          usersApi.util.updateQueryData('getUsers', undefined, (draft) => {
            return draft.filter(
              (u) => !(u.id === userId && String(u.tenantid) === String(tenantId))
            );
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      // Don't invalidate - rely on optimistic update only
      invalidatesTags: [],
    }),

    // Get project (workspace) permissions
    getProjectUsers: builder.query<TenantUser[], { tenantId: string; appId: string }>({
      query: ({ tenantId, appId }) => ({
        url: `${tenantId}/${appId}/permissions`,
        endpoint: 'main',
      }),
      transformResponse: (response: unknown) => fromApiUsers(response as Record<string, unknown>[]),
      providesTags: (result, _error, { appId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'User' as const, id })),
              { type: 'User', id: `PROJECT_${appId}` },
            ]
          : [{ type: 'User', id: `PROJECT_${appId}` }],
    }),

    // Invite users to project (workspace)
    inviteUsersToProject: builder.mutation<
      void,
      { tenantId: string; appId: string; users: InviteUsersRequest[] }
    >({
      query: ({ tenantId, appId, users }) => ({
        url: `${tenantId}/${appId}/permissions`,
        endpoint: 'main',
        method: 'POST',
        body: users, // Send array directly, not wrapped in object
      }),
      invalidatesTags: (_result, _error, { appId }) => [
        { type: 'User', id: `PROJECT_${appId}` },
        { type: 'User', id: `workspace-${appId}` }, // Also invalidate workspace tag
        { type: 'User', id: 'LIST' }, // Refetch all users to update org members list
      ],
    }),

    // Update user admin status in project
    updateProjectUserAdmin: builder.mutation<
      void,
      { tenantId: string; appId: string; userId: number; isAdmin: boolean }
    >({
      query: ({ tenantId, appId, userId, isAdmin }) => ({
        url: `${tenantId}/${appId}/permissions/${userId}`,
        endpoint: 'main',
        method: 'PUT',
        body: { isAdmin },
      }),
      onQueryStarted: async (
        { tenantId, appId, userId, isAdmin },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          usersApi.util.updateQueryData('getProjectUsers', { tenantId, appId }, (draft) => {
            const userIndex = draft.findIndex((u) => u.id === userId);
            if (userIndex !== -1) {
              draft[userIndex].admin = isAdmin;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      // Don't invalidate - rely on optimistic update only
      invalidatesTags: [],
    }),

    // Remove user from project
    removeUserFromProject: builder.mutation<
      void,
      { tenantId: string; appId: string; userId: number }
    >({
      query: ({ tenantId, appId, userId }) => ({
        url: `${tenantId}/${appId}/permissions/${userId}`,
        endpoint: 'main',
        method: 'DELETE',
      }),
      onQueryStarted: async ({ tenantId, appId, userId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          usersApi.util.updateQueryData('getProjectUsers', { tenantId, appId }, (draft) => {
            return draft.filter((u) => u.id !== userId);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      // Don't invalidate - rely on optimistic update only
      invalidatesTags: [],
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetUsersQuery,
  useInviteUsersToTenantMutation,
  useUpdateTenantUserAdminMutation,
  useRemoveUserFromTenantMutation,
  useGetProjectUsersQuery,
  useInviteUsersToProjectMutation,
  useUpdateProjectUserAdminMutation,
  useRemoveUserFromProjectMutation,
} = usersApi;
